import React, {useContext, useEffect, useState, useCallback, useRef} from "react";
import { AuthContext } from "../../../context/authContext";
import { API_BASE_URL, API_KEY } from "../../../config/config";
import axios from "axios";
import Comment from "./Comment";
import InfiniteScroll from "react-infinite-scroll-component";
import LoadingContent from "../../common/LoadingContent";
import LoadingNoMoreContent from "../../common/LoadingNoMoreContent";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import CommentFormTemplate from "./CommentFormTemplate";
import CommentForm from "./CommentForm";

const CommentList = ({ video, isActive, onClose = null, isForVideoView = false }) => {
	const { visitor } = useContext(AuthContext);

	const [commentsInitialLoaded, setCommentsInitialLoaded] = useState(false);
	const [comments, setComments] = useState([]);
	const [commentsLoading, setCommentsLoading] = useState(false);
	const [commentsPage, setCommentsPage] = useState(1);
	const [hasMoreComments, setHasMoreComments] = useState(false);
	const newestCommentRef = useRef(null);
	const scrollableContainerRef = useRef(null);

	const fetchComments = useCallback(async (isInitial = false) => {
		if (!visitor) return;

		if (commentsLoading) return; // Loading

		if (isInitial && commentsInitialLoaded) return; // Already loaded

		try {
			setCommentsLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/threads/${video.Post.thread_id}/posts?page=${commentsPage}`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials: true
			};

			const response = await axios.request(config);
			let newPosts = response.data.posts;
			if (typeof newPosts === "object") {
				newPosts = Object.values(newPosts);
			}

			// Remove duplicates based on post_id
			const updatedComments = isInitial ? newPosts : [...comments, ...newPosts];
			const uniqueComments = updatedComments.filter((comment, index, self) =>
				index === self.findIndex((c) => c.post_id === comment.post_id)
			);

			setComments(uniqueComments);

			let pagination = response.data.pagination;

			setHasMoreComments(pagination.current_page < pagination.last_page);
			setCommentsPage(commentsPage + 1);
			setCommentsLoading(false);
			setCommentsInitialLoaded(true);
		} catch (error) {
			console.error('Failed to fetch comments:', error);
			setCommentsLoading(false);
			setCommentsInitialLoaded(true);
		}
	}, [visitor, video.attachment_id, commentsLoading, commentsInitialLoaded, commentsPage]);

	useEffect(() => {
		if (isActive) {
			fetchComments(true);
		}
	}, [isActive, fetchComments]);

	const loadMoreComments = () => {
		if (!hasMoreComments) return;

		setCommentsPage(commentsPage + 1);
		fetchComments(false);
	};

	const addNewComment = (comment) => {
		setComments(prevComments => [comment, ...prevComments]);
		scrollToTop();
	};

	const scrollToNewComment = () => {
		if (newestCommentRef.current) {
			newestCommentRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	};

	const scrollToTop = () => {
		if (scrollableContainerRef.current) {
			scrollableContainerRef.current.scrollTop = 0;
		}
	};

	return (
		<div className={`w-full ${isForVideoView ? 'h-full flex flex-col' : 'absolute bottom-0 left-0 right-0 h-[90%] z-30'} bg-dark`}>
			{!isForVideoView && (
				<div className="relative w-full bg-primary p-2 text-white text-sm text-center">
					<span>{video.Thread.reply_count} {t('comments')}</span>

					{onClose !== null && (
						<FontAwesomeIcon
							onClick={onClose}
							icon={faTimes}
							className="absolute top-[50%] right-5 -translate-y-[50%] z-40 cursor-pointer"
						/>
					)}
				</div>
			)}

			<div
				ref={scrollableContainerRef}
				id={`scrollable-comment-video-${video.attachment_id}`}
				className={`p-2 ${isForVideoView ? 'flex-grow' : 'pb-[120px] h-full'} overflow-auto`}
			>
				<InfiniteScroll
					dataLength={comments.length}
					next={loadMoreComments}
					hasMore={hasMoreComments}
					loader={<LoadingContent/>}
					endMessage={<LoadingNoMoreContent/>}
					scrollableTarget={`scrollable-comment-video-${video.attachment_id}`}
				>
					{comments.map((comment, index) => {
						if (!comment.is_first_post)
							return <Comment
								ref={index === 0 ? newestCommentRef : null}
								key={index}
								post={comment}
								postId={comment.post_id}
							/>
					})}
				</InfiniteScroll>
			</div>

			{video.Thread && (
				<div className={`bg-primary p-1 ${isForVideoView ? '' : 'fixed bottom-0 left-0 right-0 z-40'}`}>
					{video.Thread.can_reply && (<CommentForm video={video} addNewComment={addNewComment} onCommentSubmit={scrollToNewComment} />)}

					{video.Thread.can_reply_template && (<CommentFormTemplate video={video} addNewComment={addNewComment} onCommentSubmit={scrollToNewComment} />)}
				</div>
			)}
		</div>
	);
}

export default CommentList;
